/**
 * Módulo del tutorial: Lector de caravanas HID (KAN-232).
 *
 * El bastón lector de caravanas electrónicas se detecta SOLO — como un
 * teclado más: pasás la caravana por el lector y el número aparece escrito
 * en el campo. Cero conexión, cero tipeo (sólo vincularlo por Bluetooth
 * una vez).
 *
 * Anclas: form-animal-caravana (campo caravana del formulario),
 * animal-hid-hint (aclaración de la sección Balanza Digital).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { sesionAbiertaConAnimales } from '../../../services/tutorial/sessionCondition';

export const lectorHidModule: TutorialModuleDef = {
  id: 'lector-hid',
  title: 'Caravanas sin tipear',
  description: 'El bastón lector de caravanas electrónicas se detecta solo: pasás la caravana y el número aparece escrito. Cero tipeo.',
  emoji: '🏷️',
  image: 'images/tutorial/lector-hid.png',
  permission: { module: 'animals', action: 'create' },
  grupo: 'produccion',
  route: '/animals/new',
  steps: [
    {
      id: 'concepto-hid',
      route: '/tutorial',
      text: 'Caravana electrónica leída con bastón = cero tipeo. El lector funciona como un teclado: pasás la caravana y el número aparece solo en el campo.',
      narration:
        'Si tus animales traen caravana electrónica, esto te va a gustar: el bastón lector funciona como un teclado invisible. Pasás la caravana por el lector, y el número aparece escrito solo en el campo de la ficha, como si alguien lo tipeara a la velocidad de la luz. Ni un dedito.',
    },
    {
      // Paso condicional: sólo aparece si el usuario ya tenía una sesión de
      // carga abierta con animales (las sesiones quedan abiertas hasta que
      // se inicia una nueva — SessionContext persiste en localStorage).
      id: 'sesion-abierta-hid',
      route: '/animals/new',
      target: '[data-ar-guide="sesion-modal-nueva"]',
      label: 'Cerrá la sesión anterior',
      showIf: sesionAbiertaConAnimales,
      text: '¡Ojo! Tenés una sesión ABIERTA de la última vez (las sesiones quedan abiertas hasta que iniciás una nueva). Tocá "Nueva Sesión" acá para cerrar esa y arrancar una limpia.',
      narration:
        'Un aviso antes de arrancar: tenés una sesión abierta de la última vez. No se perdió nada, es que las sesiones de esta app quedan abiertas hasta que vos iniciás una nueva. Tocá Nueva Sesión, el botón colorado de este aviso, y arrancamos una limpia para el bastón.',
    },
    {
      id: 'selector-carga-hid',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Comenzar a cargar',
      text: 'Arrancá la sesión de carga (Carga rápida sirve) y tocá "Comenzar a cargar" — el campo de caravana te espera en el formulario.',
      narration:
        'Primero lo de siempre: arrancá una sesión de carga, Carga rápida alcanza de sobra, y Comenzar a cargar. El campito de la caravana te espera adentro del formulario.',
    },
    {
      id: 'una-vez-bluetooth',
      route: '/animals/new',
      target: '[data-ar-guide="animal-hid-hint"]',
      label: 'Aclaración del lector (📡)',
      advanceOnClick: false,
      text: 'Preparación de una sola vez: vinculá el lector por Bluetooth en los ajustes del teléfono (o conéctalo por cable). De ahí en más, se detecta solo.',
      narration:
        'La preparación es una sola vez, y es afuera de la app: andá a los ajustes de Bluetooth de tu teléfono y vinculá el lector, o conectalo por cable si es de los viejos buenos. De ahí en adelante, la app lo detecta sola cada vez que lo encendés. Configurarlo una vez, usarlo para siempre.',
    },
    {
      id: 'pasar-la-caravana',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-caravana"]',
      label: 'Campo caravana',
      advanceOnClick: false,
      text: 'Con el lector vinculado, simplemente PASÁ la caravana por el bastón: el número se escribe solo en el formulario — ni hace falta tocar el campo caravana. Arriba se enciende "📡 Escaneando datos..." confirmando la lectura. OJO: algunos modelos pueden no funcionar todavía — si el tuyo no escribe, pasale el modelo a Atención al cliente y los desarrolladores le agregan soporte apenas puedan.',
      narration:
        'Ahora sí: con el lector ya vinculado, simplemente pasá la caravana del animal por el bastón, y el número entra solo en el formulario, dígito por dígito, sin errotes de dedo gordo y sin necesidad de tocar ningún campo: el lector sabe a dónde va. Cuando está escribiendo, arriba se enciende un cartel amarillo que dice Escaneando datos: ésa es la señal de que la lectura está entrando. Ahora, una aclaración honesta: algunos modelos de lectores pueden no funcionar todavía. Si pasás el tuyo y no escribe nada, no descartes nada: entrá al chat, tocá el auricular de Atención al cliente, y pasale la marca y el modelo de tu lector. Los desarrolladores le agregan soporte apenas puedan. La manga vuela igual.',
    },
    {
      id: 'cierre-hid',
      route: '/animals/new',
      text: 'Lector por un lado, balanza por el otro (si tenés), y la carga de una sesión completa se hace casi sin teclado.',
      narration:
        'Y pensalo en conjunto: el lector llena la caravana, y si además conectás la balanza, el peso también cae solo — tenés un módulo entero para eso. Entre los dos, la carga de una sesión se hace casi sin tocar el teclado. La tecnología al servicio del ganado, criollo, no al revés.',
    },
  ],
  completionCheck: async () => false,
};
